import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import React, { useEffect } from "react";
import AnalyticsListener from "@/components/AnalyticsListener";
import { captureTracking } from "@/lib/tracking";
import VerifiedRoute from "@/components/VerifiedRoute";
import SchemaMarkup from "@/components/SchemaMarkup";
import { buildLocalBusinessSchema, buildServiceSchema } from "@/lib/schema";
import PublicLayout from "@/components/PublicLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import LandingPage from "@/pages/LandingPageV2";
import QuoteIntakeQuiz from "@/pages/QuoteIntakeQuiz";
import ThankYou from "@/pages/ThankYou";
import CustomerStatus from "@/pages/CustomerStatus";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import ServiceAreas from "@/pages/ServiceAreas";
import CityPage from "@/pages/CityPage";
import CaseStudyKendall from "@/pages/CaseStudyKendall";
import CaseStudyNorthMiami from "@/pages/CaseStudyNorthMiami";
import CaseStudiesIndex from "@/pages/CaseStudiesIndex";
import InstallationProcess from "@/pages/InstallationProcess";
import MiamiServices from "@/pages/MiamiServices";
import InsuranceSavings from "@/pages/InsuranceSavings";
import ImpactWindowsMiami from "@/pages/ImpactWindowsMiami";
import ImpactWindowsFortLauderdale from "@/pages/ImpactWindowsFortLauderdale";
import ImpactWindowsBocaRaton from "@/pages/ImpactWindowsBocaRaton";
import GetQuoteLanding from "@/pages/GetQuoteLanding";
import LandingCostMiami from "@/pages/LandingCostMiami";
import LandingNoSales from "@/pages/LandingNoSales";
import Tier2CityPage from "@/pages/Tier2CityPage";
import { TIER2_CITIES } from "@/data/tier2Cities";

// Money Pages (AI-optimized content)
import PermitPage from "@/pages/PermitPage";

// Question Pages (AI targeting)
import CostQuestionPage from "@/pages/CostQuestionPage";
import PermitQuestionPage from "@/pages/PermitQuestionPage";

import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminLeadDetail from "@/pages/AdminLeadDetail";
import AdminQuoteBuilder from "@/pages/AdminQuoteBuilder";
import AdminSettings from "@/pages/AdminSettings";

// Scroll restoration component (must be inside BrowserRouter)
function ScrollToTop() {
  const { pathname } = useLocation();
  
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Capture UTMs / landing page / referrer once per session, refreshing UTMs
// on any new tagged URL. Mounted globally so attribution survives the funnel.
function TrackingCapture() {
  const { pathname, search } = useLocation();
  React.useEffect(() => {
    captureTracking();
  }, [pathname, search]);
  return null;
}

function App() {
  // Site-wide schemas — LocalBusiness identity + canonical Service offering.
  // SchemaMarkup gates on hostname so this only injects on snap-windows.com.
  const siteSchemas = [buildLocalBusinessSchema(), buildServiceSchema()];
  return (
    <div className="min-h-screen bg-background">
      <BrowserRouter>
        <ScrollToTop />
        <TrackingCapture />
        <AnalyticsListener />
        <SchemaMarkup schemas={siteSchemas} instanceId="site-root" />
        <Routes>
          {/* Public routes with Header/Footer */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/quote" element={<VerifiedRoute><QuoteIntakeQuiz /></VerifiedRoute>} />
            <Route path="/thank-you" element={<ThankYou />} />
            
            {/* Legal pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/service-areas" element={<ServiceAreas />} />
            
            {/* Money Pages (AI-optimized content) */}
            <Route path="/impact-window-permits-miami-dade" element={<PermitPage />} />
            
            {/* Question Pages (AI targeting) */}
            <Route path="/how-much-do-impact-windows-cost-miami" element={<CostQuestionPage />} />
            <Route path="/permits-impact-windows-miami-dade" element={<PermitQuestionPage />} />
            
            {/* Case Study Pages */}
            <Route path="/case-studies" element={<CaseStudiesIndex />} />
            <Route path="/case-study-kendall-full-house" element={<CaseStudyKendall />} />
            <Route path="/case-study-north-miami-condo" element={<CaseStudyNorthMiami />} />
            
            {/* Informational Pages */}
            <Route path="/installation-process" element={<InstallationProcess />} />
            <Route path="/impact-window-installation-process" element={<InstallationProcess />} />
            <Route path="/miami-services" element={<MiamiServices />} />
            <Route path="/insurance-savings" element={<InsuranceSavings />} />
            
            {/* City/SEO Pages */}
            <Route path="/impact-windows-miami" element={<ImpactWindowsMiami />} />
            <Route path="/impact-windows-fort-lauderdale" element={<ImpactWindowsFortLauderdale />} />
            <Route path="/impact-windows-boca-raton" element={<ImpactWindowsBocaRaton />} />
            
            {/* City SEO pages - FIXED dynamic route with parameter */}
            <Route path="/impact-windows/:citySlug" element={<CityPage />} />
          </Route>
          
          {/* Routes without Header/Footer — Paid traffic landing pages + Tier 2 SEO */}
          <Route path="/get-quote" element={<GetQuoteLanding />} />
          <Route path="/impact-window-cost-miami" element={<LandingCostMiami />} />
          <Route path="/no-sales-impact-windows" element={<LandingNoSales />} />

          {/* Tier 2 SEO city pages (each funnels to /get-quote) */}
          <Route path="/impact-windows-hialeah" element={<Tier2CityPage city={TIER2_CITIES.hialeah} />} />
          <Route path="/impact-windows-doral" element={<Tier2CityPage city={TIER2_CITIES.doral} />} />
          <Route path="/impact-windows-kendall" element={<Tier2CityPage city={TIER2_CITIES.kendall} />} />
          <Route path="/impact-windows-coral-gables" element={<Tier2CityPage city={TIER2_CITIES['coral-gables']} />} />
          <Route path="/impact-windows-pembroke-pines" element={<Tier2CityPage city={TIER2_CITIES['pembroke-pines']} />} />
          <Route path="/impact-windows-hollywood" element={<Tier2CityPage city={TIER2_CITIES.hollywood} />} />
          <Route path="/impact-windows-west-palm-beach" element={<Tier2CityPage city={TIER2_CITIES['west-palm-beach']} />} />

          <Route path="/status/:token" element={<CustomerStatus />} />
          
          {/* Auth */}
          <Route path="/admin/login" element={<AdminLogin />} />
          
          {/* Protected admin routes */}
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/leads/:id" element={<ProtectedRoute><AdminLeadDetail /></ProtectedRoute>} />
          <Route path="/admin/quotes/new/:leadId" element={<ProtectedRoute><AdminQuoteBuilder /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
